import React, { FC, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import DataTable, { createDataRow, HeadCell } from "@/components/DataTable";
import ActionMenu from "@/components/ActionMenu";
import GenericAlertModal from "@/components/AlertModal";

import useDebounce from "@/hooks/useDebounce";
import { usePageStore } from "@/hooks/usePageStore";
import { useUserStore } from "@/hooks/useUserStore";
import {
  useAuditLogFirstRender,
  useCreateAuditlog,
} from "@/hooks/useAuditLogs";
import {
  useSettingsCatalog,
  useArchiveSetting,
  useCreateSetting,
  useUpdateSetting,
} from "@/hooks/useSettingCatalog";

import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import { useAppTheme } from "@/utils/theme";
import { settingValueToDisplay } from "@/utils/settingValue";

import { Setting, SettingPayload } from "@/domain/settingCatalog.interface";
import { ErrorResponse } from "@/domain/error.interface";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";

import SettingFilters, { Filters } from "@/ui/content/settingsCatalog/Filters";
import SettingModal from "@/ui/modals/settingsCatalog/SettingModal";

const DEFAULT_FILTERS: Filters = {
  sortField: "name",
  sortOrder: 1,
  pageNumber: 0,
  pageSize: 10,
  search: "",
  application: "",
  type: "",
};

const SettingsCatalogPage: FC = () => {
  const theme = useAppTheme();
  const { showSnackBar } = useFeedBack();
  const { userInfo } = useUserStore();
  const { setPageTitle } = usePageStore();

  const filtersState = useState<Filters>(DEFAULT_FILTERS);
  const [filters, setFilters] = filtersState;
  const debouncedSearch = useDebounce({ value: filters.search });

  const [modalState, setModalState] = useState<{
    open: boolean;
    setting?: Setting;
  }>({ open: false });

  const [deleteAlert, setDeleteAlert] = useState<{
    open: boolean;
    setting?: Setting;
  }>({ open: false });

  const { settings, isLoadingSettings, isFetchingSettings, refetchSettings } =
    useSettingsCatalog({
      sortField: filters.sortField,
      sortOrder: filters.sortOrder,
      pageNumber: filters.pageNumber,
      pageSize: filters.pageSize,
      search: debouncedSearch,
      application: filters.application,
      type: filters.type,
    });

  const createSetting = useCreateSetting();
  const updateSetting = useUpdateSetting();
  const archiveSetting = useArchiveSetting();
  const createAuditLog = useCreateAuditlog();

  const isSaving = createSetting.isPending || updateSetting.isPending;

  useEffect(() => {
    setPageTitle("Settings catalog");
  }, []);

  useAuditLogFirstRender({
    appType: "WEB_BACK_OFFICE",
    module: MODULE_NAME.SETTINGS,
    option: OPTIONS.SETTINGS_CATALOG_LIST,
    actionCode: ACTION_CODE.WEB_SETTING_CAT_LIST_VIEW,
    action: "When entering",
    detail: "Viewed the settings catalog",
    transactionDate: new Date(),
    accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
    createdBy: userInfo?._id as string,
    programId: userInfo?.programId as string,
    orgUnitId: userInfo?.orgUnitIds?.[0] as string,
  });

  useEffect(() => {
    refetchSettings();
  }, [
    debouncedSearch,
    filters.application,
    filters.type,
    filters.sortField,
    filters.sortOrder,
    filters.pageNumber,
    filters.pageSize,
  ]);

  useEffect(() => {
    if (debouncedSearch !== "") {
      createAuditLog.mutate({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.SETTINGS,
        option: OPTIONS.SETTINGS_CATALOG_LIST,
        actionCode: ACTION_CODE.WEB_SETTING_CAT_LIST_SEARCH,
        action: "When searching",
        detail: "Searched within the settings catalog",
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: userInfo?._id as string,
      });
    }
  }, [debouncedSearch]);

  const handleOpenCreate = () => {
    setModalState({ open: true, setting: undefined });
  };

  const handleOpenEdit = (setting: Setting) => {
    setModalState({ open: true, setting });
  };

  const handleCloseModal = () => {
    setModalState({ open: false, setting: undefined });
  };

  const handleSave = async (data: SettingPayload) => {
    const editing = modalState.setting;

    try {
      if (editing) {
        await updateSetting.mutateAsync({ id: editing._id, payload: data });

        createAuditLog.mutate({
          appType: "WEB_BACK_OFFICE",
          module: MODULE_NAME.SETTINGS,
          option: OPTIONS.SETTING_ADD_EDIT,
          actionCode: ACTION_CODE.WEB_SETTING_CAT_EDIT,
          action: "When editing a setting",
          detail: `Edited setting ${data.name}`,
          transactionDate: new Date(),
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          createdBy: userInfo?._id as string,
        });

        showSnackBar("The setting has been updated.", "success");
      } else {
        await createSetting.mutateAsync(data);

        createAuditLog.mutate({
          appType: "WEB_BACK_OFFICE",
          module: MODULE_NAME.SETTINGS,
          option: OPTIONS.SETTING_ADD_EDIT,
          actionCode: ACTION_CODE.WEB_SETTING_CAT_ADD,
          action: "When creating a setting",
          detail: `Created setting ${data.name}`,
          transactionDate: new Date(),
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          createdBy: userInfo?._id as string,
        });

        showSnackBar("The setting has been created.", "success");
      }

      handleCloseModal();
      refetchSettings();
    } catch (error: unknown) {
      const err = error as ErrorResponse;
      showSnackBar(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
          err.error ||
          "An error occurred"
      );
    }
  };

  const handleConfirmDelete = async () => {
    const target = deleteAlert.setting;
    if (!target) return;

    try {
      await archiveSetting.mutateAsync(target._id);

      createAuditLog.mutate({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.SETTINGS,
        option: OPTIONS.DELETE,
        actionCode: ACTION_CODE.WEB_SETTING_CAT_DELETE,
        action: "When deleting a setting",
        detail: `Deleted setting ${target.name}`,
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: userInfo?._id as string,
      });

      showSnackBar("The registry has been deleted.", "success");
      setDeleteAlert({ open: false });
      refetchSettings();
    } catch (error: unknown) {
      const err = error as ErrorResponse;
      showSnackBar(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
          err.error ||
          "An error occurred"
      );
    }
  };

  const headCells: HeadCell[] = [
    {
      id: "name",
      isSortable: true,
      disablePadding: true,
      label: "Name",
      align: "left",
      width: "200px",
    },
    {
      id: "application",
      isSortable: true,
      disablePadding: false,
      label: "Application",
      align: "left",
      width: "120px",
    },
    {
      id: "module",
      isSortable: true,
      disablePadding: false,
      label: "Module / Section",
      align: "left",
      width: "180px",
    },
    {
      id: "type",
      isSortable: true,
      disablePadding: false,
      label: "Type",
      align: "left",
      width: "100px",
    },
    {
      id: "default",
      isSortable: false,
      disablePadding: false,
      label: "Default",
      align: "left",
    },
    {
      id: "active",
      isSortable: true,
      disablePadding: false,
      label: "Active",
      align: "left",
      width: "100px",
    },
    {
      id: "action",
      isSortable: false,
      disablePadding: false,
      label: "Actions",
      align: "right",
      width: "100px",
    },
  ];

  const rows = useMemo(() => {
    if (!settings || settings.list.length === 0) return [];

    return settings.list.map((row, index) => {
      const actionMenu = [
        {
          value: "Edit",
          onClick: () => handleOpenEdit(row),
          icon: <EditIcon fontSize="small" />,
        },
        {
          value: "Delete",
          onClick: () => setDeleteAlert({ open: true, setting: row }),
          icon: <DeleteOutlineIcon fontSize="small" />,
          sx: {
            color: theme.palette.error.main,
          },
        },
      ];

      return createDataRow(index, {
        name: <div>{row.name}</div>,
        application: <div>{row.application}</div>,
        module: (
          <div>
            {row.module}
            {row.section ? ` / ${row.section}` : ""}
          </div>
        ),
        type: <div>{row.type}</div>,
        default: (
          <div className="max-w-[280px] truncate">
            {settingValueToDisplay(row.type, row.default)}
          </div>
        ),
        active: (
          <Chip
            sx={{
              bgcolor: row.active
                ? theme.palette.brand.light
                : theme.palette.warning.light,
              borderWidth: 1,
              borderStyle: "solid",
              borderColor: row.active
                ? theme.palette.brand.main
                : theme.palette.warning.dark,
            }}
            label={
              <div className="text-xs">
                {row.active ? "Active" : "Inactive"}
              </div>
            }
          />
        ),
        action: <ActionMenu id={row._id} options={actionMenu} />,
      });
    });
  }, [settings, theme]);

  return (
    <>
      <div className="w-full overflow-y-auto scroll-smooth">
        <div className="full-width-container mx-auto">
          <div className="flex flex-col items-center justify-start min-h-screen space-y-5">
            <Card sx={{ width: "100%", borderRadius: "8px" }}>
              <CardContent
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  rowGap: "20px",
                }}
              >
                <Box
                  sx={{ width: "100%" }}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="headlineSmall" component="h1">
                    Settings catalog
                  </Typography>

                  <Button
                    size="medium"
                    onClick={handleOpenCreate}
                    variant="contained"
                    sx={{
                      borderRadius: "8px",
                      textTransform: "none",
                      textAlign: "center",
                      height: "40px",
                      backgroundColor: theme.palette.primary.dark,
                      color: theme.palette.textMain.light,
                    }}
                    startIcon={<AddIcon />}
                  >
                    ADD
                  </Button>
                </Box>

                <SettingFilters filtersState={filtersState} />

                <DataTable
                  data={rows}
                  headCells={headCells}
                  order={filters.sortOrder === 1 ? "asc" : "desc"}
                  orderBy={filters.sortField}
                  rowsPerPage={filters.pageSize}
                  page={filters.pageNumber}
                  total={settings?.totalCount}
                  isLoading={isLoadingSettings || isFetchingSettings}
                  messageEmptyData="No settings to show."
                  onChangePage={(_event, newPage) => {
                    setFilters((prev) => ({ ...prev, pageNumber: newPage }));
                  }}
                  onChangeSize={(event) => {
                    setFilters((prev) => ({
                      ...prev,
                      pageNumber: 0,
                      pageSize: Number(event.target.value),
                    }));
                  }}
                  onChangeSort={(order, orderBy) => {
                    setFilters((prev) => {
                      const newSortField = orderBy as string;
                      const newSortOrder = order === "asc" ? 1 : -1;
                      if (
                        prev.sortField === newSortField &&
                        prev.sortOrder === newSortOrder
                      ) {
                        return prev;
                      }

                      return {
                        ...prev,
                        pageNumber: 0,
                        sortField: newSortField,
                        sortOrder: newSortOrder,
                      };
                    });
                  }}
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {modalState.open && (
        <SettingModal
          open={modalState.open}
          onClose={handleCloseModal}
          setting={modalState.setting}
          onSave={handleSave}
          isPending={isSaving}
        />
      )}

      {deleteAlert.open && (
        <GenericAlertModal
          onCancel={() => setDeleteAlert({ open: false })}
          onClick={handleConfirmDelete}
          title="Delete"
          description="Are you sure you want to delete this registry?"
          submitText="Confirm"
          cancelText="Cancel"
          isLoading={archiveSetting.isPending}
          theme={theme}
        />
      )}
    </>
  );
};

export default SettingsCatalogPage;
